import type { LifecycleStatus,RunnabilityStatus, EntityType, KyxRiskModelEntityRelationship } from './enums';
import type { RiskLevel } from './risk';

export interface BaseForWizard {
    isEditMode: boolean;
    isClone: boolean;
    name: string | null;
    originalName: string;
    entityType: EntityType;
    entity_scope: any;
    relationship_type_ids?: string[]; // Optional for templates
    configuration: any;
    based_on_instance_id: string | null;
    default_outdated_threshold_value: string;
    default_outdated_threshold_type: string;
    base_token_price: number;
    decision_logic: DecisionLogic | null;
}

export type { RiskLevel };
export type Operator = 'GREATER_THAN' | 'LESS_THAN' | 'OUTSIDE_RANGE' | 'EQUAL_TO' | 'IS_IN_LIST' | 'IS_NOT_IN_LIST' | 'BETWEEN';



export interface BaseRiskProfileRule {
  id: string;
  field: string | null;
  operator: Operator | null;
  value: any;
  status: RunnabilityStatus;
}
export interface RiskProfileRule extends BaseRiskProfileRule {
  field: string;
  operator: Operator;
}

export interface BaseRiskProfileRuleScoreMapping {
  id: string;
  riskLevel: number;
  conditions: BaseRiskProfileRule[];
  scope: string[];
}

export interface RiskProfileRuleScoreMapping extends BaseRiskProfileRuleScoreMapping {
  conditions: RiskProfileRule[];
  isNew?: boolean;
}

export interface UIRule extends RiskProfileRuleScoreMapping {
  isNew: boolean;
}



export interface UIRiskProfileScopeRule extends BaseRiskProfileRule {
  enabled?: boolean
  is_scope_input_required: boolean
  rule_id: string | null
  status: RunnabilityStatus;
}




export interface UIRiskProfileCategoryInstanceConfiguration {
  validity: {
    value: number;
    unit: string;
  };
  scope: {
    conditions: UIRiskProfileScopeRule[];
  };
  rules: RiskProfileRuleScoreMapping[];
  defaultRisk: number;
}

export interface UIRiskProfileCategoryInstance{
  id: string;
  name: string;
  checkType: string;
  enabled: boolean;
  entity_relationships: string[];
  configuration: UIRiskProfileCategoryInstanceConfiguration;
  category_id: string;
  status: RunnabilityStatus;
}

export interface UIRiskProfileCategory {
  entity_relationships: {
    value: string;
    enabled: boolean;
    label: string;
  }[];
  checks: UIRiskProfileCategoryInstance[];
}

// Represents the data structure for an editable risk profile (e.g., in a wizard or form)
export interface EditableRiskProfile {
  name: string | null;
  type: EntityType | null;
  relationship_type_ids: string[];
  default_outdated_threshold_value: string;
  default_outdated_threshold_type: string;
  configuration: UIRiskProfileCategory;
  decision_logic: DecisionLogic | null;
}

// --- Risk Model Instance Types (from API responses) ---

export interface RiskModelInstanceRule {
  id: string;
  rule_logic: RuleGroupPayload;
  risk_level_id: string;
  entity_relationship: KyxRiskModelEntityRelationship[];
  priority: number;
  status?: string;
}

export interface RiskModelInstanceScopeRule {
  rule_logic: RuleGroupPayload & { status?: string };
  entity_relationship: KyxRiskModelEntityRelationship[];
  default_risk_level_id: string;
}

export interface RiskModelInstanceCategory {
  id: string;
  name: string;
  category_slug: string;
  category_status: RunnabilityStatus;
  default_outdated_threshold_value: string;
  default_outdated_threshold_type: string;
  scope_rules: RiskModelInstanceScopeRule[];
  risk_rules: RiskModelInstanceRule[];
}



// --- Automatic Decisioning Types ---
export type DecisionResult = 'approve' | 'decline' | 'manual';

// How each automatic decision result reads wherever it is shown.
export const DECISION_RESULT_LABELS: Record<DecisionResult, string> = {
  approve: 'Aprovar',
  decline: 'Recusar',
  manual: 'Manual',
};

// The risk levels the decision logic operates on (slugs from risk_levels.yaml).
// The system only auto-decides on these four; no_risk / very_low are intentionally not used here.
export const EXPOSED_DECISION_RISK_LEVEL_SLUGS = ['low', 'medium', 'high', 'very_high'] as const;

export interface DecisionRule {
  risk_level_slug: string;
  result: DecisionResult;
}

export interface DecisionLogic {
  // v1 is a simple risk-level -> result lookup over the four operated levels
  // (low..very_high). Any level not listed is implicitly manual.
  rules: DecisionRule[];
}

export interface RiskModelInstance {
    id: string;
    status: LifecycleStatus;
    version_number: number | null;
    entity_scope: any; // TODO: Should be refined based on actual data structure
    categories: RiskModelInstanceCategory[];
    base_token_price: number;
    decision_logic?: DecisionLogic | null;
    decision_json_version?: number | null;
    decision_version?: number | null;
    [key: string]: any; // Allow other properties
}

export interface ProfileGroup {
  profileGroupId: string
  profileName: string
  entity_type: EntityType
  isActive: boolean
  deletedAt?: string | null
  createdAt: string // ISO date string
  currentInstanceAuthorName?: string | null
  last_updated_at: string // ISO date string
  default_outdated_threshold_value: string
  default_outdated_threshold_type: string
  instances: RiskModelInstance[]
  currentInstanceId: string | null
  [key: string]: any // Allow other properties
}

// Query params for the paginated, server-filtered profile-group / template list endpoints.
export interface ProfileGroupListParams {
  offset?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'deleted' | 'all';
  /** On/off filter; omitted = both. */
  is_active?: boolean;
  sort_field?: string;
  sort_order?: 'asc' | 'desc';
}

// Envelope returned by the list endpoints. `items` are lightweight summaries that omit the
// full rule config; they are structurally a subset of ProfileGroup and safe to read as such.
export interface PaginatedProfileGroups {
  items: ProfileGroup[];
  total: number;
}

export interface ProfileGroupDeleteImpact {
  profileGroupId: string
  profileName: string
  entity_type: EntityType
  reportCount: number
  affectedEntityCount: number
}

// --- API Payload Types ---

// Using "Payload" suffix to indicate these are for API requests
export interface RuleLeafPayload {
  type: 'LEAF';
  rule_id?: string;
  source_field_slug: string;
  parameters: Record<string, any>;
  status: RunnabilityStatus;
}

export interface RuleGroupPayload {
  type: 'GROUP';
  operator: 'AND' | 'OR';
  children: Array<RuleGroupPayload | RuleLeafPayload>;
}

export interface RiskProfileCategoryRulePayload {
  rule_logic: RuleGroupPayload;
  entity_relationship?: KyxRiskModelEntityRelationship[];
  risk_level_id: string | null;
  priority: number;
}

export interface RiskProfileCategoryScopePayload {
  rule_logic: RuleGroupPayload;
  entity_relationship?: KyxRiskModelEntityRelationship[];
  default_risk_level_id: string;
}

export interface RiskProfileCategoryInstancePayload {
  category_id: string;
  category_slug: string;
  name: string;
  default_outdated_threshold_value: string;
  default_outdated_threshold_type: string;
  scope_rules?: RiskProfileCategoryScopePayload[];
  risk_rules?: RiskProfileCategoryRulePayload[];
}

export interface RiskProfileInstancePayload {
  version_number?: number;
  instance_lifecycle?: LifecycleStatus;
  entity_scope?: KyxRiskModelEntityRelationship[];
  categories?: RiskProfileCategoryInstancePayload[];
  default_outdated_threshold_value?: string;
  default_outdated_threshold_type?: string;
  decision_logic?: DecisionLogic | null;
}

export interface RiskProfileInstanceDraftPayload {
  entity_scope?: KyxRiskModelEntityRelationship[];
  categories?: RiskProfileCategoryInstancePayload[];
  based_on_instance_id?: string;
  default_outdated_threshold_value?: string;
  default_outdated_threshold_type?: string;
  decision_logic?: DecisionLogic | null;
}

// This is the main payload for creating a new risk profile from scratch
export interface RiskProfileCreatePayload {
  model_name: string;
  description?: string;
  entity_type: EntityType;
  is_active?: boolean;
  instance: RiskProfileInstancePayload;
  relationship_type_ids?: string[];
}
